import { useEffect, useRef, useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import DiscoveryRadiusMap from '../../components/DiscoveryRadiusMap';
import ThemeCoveragePanel from '../../components/ThemeCoveragePanel';
import OrganiserDiscoveryPanel from '../../components/OrganiserDiscoveryPanel';
import { api } from '../../lib/api';

/**
 * AdminCoveragePage — `/admin/coverage`
 *
 * One place to configure the geographic footprint of the site:
 *  - Home base address (center of the discovery circle / map)
 *  - Discovery radius (meters) for Google Places venue discovery
 *  - Neighborhood polygons (overrides the built-in GeoJSON)
 *
 * Replaces the old "Neighborhoods" admin page (`/admin/polygons`) and absorbs
 * the radius / home-base inputs that previously lived in Settings.
 */

interface CoverageSettings {
  discoveryRadiusMeters: number;
  homeBaseAddress?: string;
}

interface PolygonResponse {
  overrideActive: boolean;
  updatedAt: string | null;
  geojson: unknown;
}

interface SaveResponse {
  overrideActive: boolean;
  count: number;
  updatedAt: string;
}

const GEOJSON_IO_URL = 'https://geojson.io';

export default function AdminCoveragePage() {
  // Coverage settings (radius + home base).
  const [settings, setSettings] = useState<CoverageSettings | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSavedAt, setSettingsSavedAt] = useState<number | null>(null);

  // Polygons editor.
  const [data, setData] = useState<PolygonResponse | null>(null);
  const [editor, setEditor] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ kind: 'idle' | 'success' | 'error'; msg: string }>({ kind: 'idle', msg: '' });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadSettings = async () => {
    try {
      const s = await api.get<CoverageSettings>('/api/mgmt/settings');
      setSettings({
        discoveryRadiusMeters: s.discoveryRadiusMeters ?? 0,
        homeBaseAddress: s.homeBaseAddress ?? '',
      });
    } catch {
      // Fail-soft: leave null, user can retry.
    }
  };

  const loadPolygons = async () => {
    try {
      const resp = await api.get<PolygonResponse>('/api/mgmt/neighborhood-polygons');
      setData(resp);
      setEditor(JSON.stringify(resp.geojson, null, 2));
    } catch (e) {
      setStatus({ kind: 'error', msg: e instanceof Error ? e.message : 'Failed to load polygons' });
    }
  };

  useEffect(() => {
    void loadSettings();
    void loadPolygons();
  }, []);

  const saveSettings = async () => {
    if (!settings) return;
    setSavingSettings(true);
    try {
      await api.patch('/api/mgmt/settings', settings);
      setSettingsSavedAt(Date.now());
    } finally {
      setSavingSettings(false);
    }
  };

  const copyCurrent = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(data.geojson, null, 2));
      setStatus({ kind: 'success', msg: 'Copied current GeoJSON to clipboard.' });
    } catch {
      setStatus({ kind: 'error', msg: 'Clipboard copy failed — select & copy from the editor below.' });
    }
  };

  const onFile = async (file: File) => {
    try {
      const text = await file.text();
      JSON.parse(text);
      setEditor(text);
      setStatus({ kind: 'success', msg: `Loaded ${file.name} into the editor. Click Save to apply.` });
    } catch (e) {
      setStatus({ kind: 'error', msg: 'Selected file is not valid JSON: ' + (e instanceof Error ? e.message : 'parse error') });
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) void onFile(f);
    e.target.value = '';
  };

  const savePolygons = async () => {
    setBusy(true);
    setStatus({ kind: 'idle', msg: '' });
    try {
      const parsed = JSON.parse(editor);
      const resp = await api.put<SaveResponse>('/api/mgmt/neighborhood-polygons', { geojson: parsed });
      setStatus({ kind: 'success', msg: `Saved ${resp.count} polygon${resp.count === 1 ? '' : 's'}. Active immediately.` });
      await loadPolygons();
    } catch (e) {
      setStatus({ kind: 'error', msg: e instanceof Error ? e.message : 'Save failed' });
    } finally {
      setBusy(false);
    }
  };

  const revert = async () => {
    if (!confirm('Remove the override and revert to the built-in polygons?')) return;
    setBusy(true);
    setStatus({ kind: 'idle', msg: '' });
    try {
      await api.del<void>('/api/mgmt/neighborhood-polygons');
      setStatus({ kind: 'success', msg: 'Reverted to built-in polygons.' });
      await loadPolygons();
    } catch (e) {
      setStatus({ kind: 'error', msg: e instanceof Error ? e.message : 'Revert failed' });
    } finally {
      setBusy(false);
    }
  };

  const overrideActive = data?.overrideActive === true;

  return (
    <AdminLayout>
      <h1>Coverage</h1>

      {settings && (
        <section className="mb-5">
          <h2 className="h4">Discovery area</h2>
          <div className="mb-3">
            <label htmlFor="home-base" className="form-label">Home base address</label>
            <input
              id="home-base"
              type="text"
              className="form-control mh-input-wide"
              value={settings.homeBaseAddress ?? ''}
              onChange={(e) => setSettings({ ...settings, homeBaseAddress: e.target.value })}
              aria-describedby="home-base-help"
            />
            <div id="home-base-help" className="form-text">
              Center point of the discovery circle. Used by Google Places venue discovery and the map preview below.
            </div>
          </div>
          <div className="mb-3">
            <label htmlFor="radius" className="form-label">Discovery radius (meters)</label>
            <input
              id="radius"
              type="number"
              min={100}
              max={50000}
              className="form-control mh-input-narrow"
              value={settings.discoveryRadiusMeters}
              onChange={(e) => setSettings({ ...settings, discoveryRadiusMeters: parseInt(e.target.value, 10) })}
            />
            <div className="mt-2">
              <DiscoveryRadiusMap
                address={settings.homeBaseAddress?.trim() || '1257 Glen Laura Road'}
                radiusMeters={settings.discoveryRadiusMeters || 0}
              />
            </div>
          </div>
          <button className="btn btn-primary" onClick={saveSettings} disabled={savingSettings}>
            {savingSettings ? 'Saving…' : 'Save'}
          </button>
          {settingsSavedAt && (
            <span className="ms-3 text-success" role="status">
              ✓ Saved {new Date(settingsSavedAt).toLocaleTimeString()}
            </span>
          )}
        </section>
      )}

      <ThemeCoveragePanel />

      <OrganiserDiscoveryPanel />

      <section>
        <h2 className="h4">Neighborhood polygons</h2>        <div className="alert alert-info" role="status">
          <strong>Active source:</strong>{' '}
          {overrideActive
            ? <>Custom override (saved {data?.updatedAt ? new Date(data.updatedAt).toLocaleString() : 'unknown'})</>
            : <>Built-in defaults shipped with the app</>}
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <h3 className="h6">How to edit polygons in geojson.io</h3>
            <ol className="mb-3">
              <li>Click <strong>Copy current GeoJSON</strong> below.</li>
              <li>
                Open{' '}
                <a href={GEOJSON_IO_URL} target="_blank" rel="noopener noreferrer">geojson.io</a>
                {' '}in a new tab.
              </li>
              <li>In the right-hand panel choose the <em>JSON</em> tab and replace its contents with what you copied.</li>
              <li>Edit visually. Each polygon needs a <code>name</code> property (set via the feature's properties).</li>
              <li>Copy the JSON back here — or use <em>Save → GeoJSON</em> and upload the file with the file picker below.</li>
              <li>Click <strong>Save override</strong>. New polygons take effect immediately.</li>
            </ol>
            <div className="d-flex flex-wrap gap-2">
              <button type="button" className="btn btn-outline-primary" onClick={copyCurrent} disabled={!data}>
                Copy current GeoJSON
              </button>
              <a className="btn btn-outline-secondary" href={GEOJSON_IO_URL} target="_blank" rel="noopener noreferrer">
                Open geojson.io
              </a>
              <button type="button" className="btn btn-outline-secondary" onClick={() => fileInputRef.current?.click()}>
                Upload .geojson file
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".geojson,.json,application/geo+json,application/json"
                className="d-none"
                onChange={onFileChange}
                aria-label="Upload edited GeoJSON file"
              />
            </div>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <h3 className="h6">Editor</h3>
            <p className="text-muted small mb-2">
              Paste a FeatureCollection here (or load one with the file picker above), then click <strong>Save override</strong>.
              Each Feature must have <code>geometry.type === &quot;Polygon&quot;</code> and <code>properties.name</code>.
            </p>
            <label htmlFor="polygon-editor" className="visually-hidden">GeoJSON editor</label>
            <textarea
              id="polygon-editor"
              className="form-control font-monospace"
              rows={18}
              spellCheck={false}
              value={editor}
              onChange={(e) => setEditor(e.target.value)}
            />
            <div className="d-flex flex-wrap gap-2 mt-3">
              <button type="button" className="btn btn-primary" onClick={savePolygons} disabled={busy || !editor.trim()}>
                {busy ? 'Saving…' : 'Save override'}
              </button>
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={revert}
                disabled={busy || !overrideActive}
                title={overrideActive ? 'Remove the override' : 'No override is currently active'}
              >
                Revert to built-in
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={() => void loadPolygons()} disabled={busy}>
                Reload from server
              </button>
            </div>
            {status.msg && (
              <div
                role={status.kind === 'error' ? 'alert' : 'status'}
                className={`alert mt-3 mb-0 ${status.kind === 'error' ? 'alert-danger' : 'alert-success'}`}
              >
                {status.msg}
              </div>
            )}
          </div>
        </div>
      </section>
    </AdminLayout>
  );
}
